// BarGraph5.js
import React from "react";

export const BarGraph2 = ({ data }) => {
  if (!data) return null;

  const {
    caption = "",
    title = "",
    xAxis = { label: "", categories: [] }, // Years (1980, 1990, 2000)
    yAxis = { label: "", ticks: [] },
    bars = [],
    colors = []
  } = data;

  // 1) Y-Axis Scaling: y=96 (Max) to y=296 (Min)
  const yTop = 96;
  const yBottom = 296;
  const chartHeight = yBottom - yTop; // 200

  // Get Maximum Y-Axis Value
  const maxData = yAxis.ticks[0] || 550;

  // 2) X-Axis Spacing
  const xLeft = 116.85;
  const xRight = 385;
  const chartWidth = xRight - xLeft;

  // Extract unique years from bars
  const years = xAxis.categories; // ["1980", "1990", "2000"]
  const numYears = years.length;  // 3 years
  const numRobots = 3;            // Each year has 3 robots

  // Calculate Spacing for grouped bars
  const groupWidth = (chartWidth - (numYears + 1) * 10) / numYears; // Space per year
  const barWidth = groupWidth / numRobots; // Space per individual bar

  // Function to scale Y values
  const scaleY = (value) => yBottom - (value / maxData) * chartHeight;

  // Render Bars for Multiple Years
  const renderBars = () => {
    return bars.map((bar, i) => {
      const { year, robot, value } = bar;

      // Find year index
      const yearIndex = years.indexOf(year);
      if (yearIndex === -1) return null;

      // Determine Group Position
      const groupX = xLeft + (yearIndex * (groupWidth + 10)); // Add spacing between year groups

      // Determine individual bar position within group
      const robotIndex = i % numRobots;
      const xPos = groupX + robotIndex * barWidth;

      // Compute bar dimensions
      const barTop = scaleY(value);
      const barHeight = yBottom - barTop;

      // Assign color by robot type
      const barColor = colors[robotIndex] || "#666666";

      return (
        <rect
          key={`bar-${year}-${robot}`}
          x={xPos}
          y={barTop}
          width={barWidth}
          height={barHeight}
          fill={barColor}
          stroke="#000"
          strokeWidth="0.9"
        />
      );
    });
  };

  // Render Y-Axis
  const renderYAxis = () => (
    <g>
      <text
        fill="#000"
        fontFamily="Crimson Text"
        fontSize="19.84"
        textAnchor="middle"
        transform="translate(24 196) rotate(-90)"
      >
        {yAxis.label}
      </text>
      <line
        fill="none"
        stroke="#000"
        strokeLinecap="round"
        strokeWidth="0.9"
        x1={xLeft}
        x2={xLeft}
        y1={yTop}
        y2={yBottom}
      />
      {yAxis.ticks.map((tick) => {
        const yPos = scaleY(tick);
        return (
          <g key={`y-tick-${tick}`}>
            <line
              fill="none"
              stroke="#000"
              strokeLinecap="round"
              strokeWidth="0.9"
              x1={xLeft}
              x2={xRight}
              y1={yPos}
              y2={yPos}
            />
            <line
              fill="none"
              stroke="#000"
              strokeLinecap="round"
              strokeWidth="0.9"
              x1={xLeft - 6}
              x2={xLeft}
              y1={yPos}
              y2={yPos}
            />
            <text
              fill="#000"
              fontFamily="Crimson Text"
              fontSize="19.84"
              textAnchor="end"
              transform={`translate(${xLeft - 12} ${yPos + 6})`}
            >
              {tick}
            </text>
          </g>
        );
      })}
    </g>
  );

  // Render X-Axis (Year Labels)
  const renderXAxis = () => (
    <g>
      <line
        fill="none"
        stroke="#000"
        strokeLinecap="round"
        strokeWidth="0.9"
        x1={xLeft}
        x2={xRight}
        y1={yBottom}
        y2={yBottom}
      />
      <line
        fill="none"
        stroke="#000"
        strokeLinecap="round"
        strokeWidth="0.9"
        x1={xLeft - 6}
        x2={xLeft}
        y1={yBottom}
        y2={yBottom}
      />
      <text
        fill="#000"
        fontFamily="Crimson Text"
        fontSize="19.84"
        textAnchor="middle"
        transform="translate(250.92434692382812 349.68)"
      >
        {xAxis.label}
      </text>
      {years.map((year, i) => {
        const xPos = xLeft + (i * (groupWidth + 10)) + groupWidth / 2;
        return (
          <text
            key={`x-year-${year}`}
            fill="#000"
            fontFamily="Crimson Text"
            fontSize="19.84"
            textAnchor="middle"
            transform={`translate(${xPos} 315.84)`}
          >
            {year}
          </text>
        );
      })}
    </g>
  );

  // Render Graph Title
  const renderTitle = () => (
    <text
      fill="#000"
      fontFamily="Crimson Text"
      fontSize="19.84"
      textAnchor="middle"
      transform="translate(250.92434692382812 24)"
    >
      {title}
    </text>
  );

  // Render Legend
  const renderLegend = () => {
    if (!colors || colors.length < numRobots) return null;
    const robotNames = ["Robby", "Robby2", "Robby3"];
    return (
      <g>
        <rect
          fill="none"
          x="141.25286865234375"
          y="366.84"
          width="132.4942626953125"
          height="103"
          stroke="#000"
          strokeLinejoin="mitre"
          strokeWidth="0.9"
        />
        {robotNames.map((robot, i) => {
          const itemX = 148.25;
          const itemY = 378.84 + i * 32;
          return (
            <g key={`legend-${robot}`}>
              <rect
                fill={colors[i]}
                x={itemX}
                y={itemY}
                width="12"
                height="12"
                stroke="#000"
                strokeLinejoin="mitre"
                strokeWidth="0.9"
              />
              <text
                fill="#000"
                fontFamily="Crimson Text"
                fontSize="19.84"
                textAnchor="top"
                transform={`translate(${itemX + 22} ${itemY + 12})`}
              >
                {robot}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <figure className="image">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="400"
        height="480.84"
        viewBox="0 0 400 480.84"
        role="img"
        aria-label={caption}
      >
        <g id="ed420550-79eb-48d4-af01-cd27cdd08afd" data-name="Layer 1">
          {renderYAxis()}
          {renderXAxis()}
          {renderBars()}
          {renderTitle()}
          {renderLegend()}
        </g>
      </svg>
    </figure>
  );
};

export default BarGraph2;
